import { useId } from 'react';
import { t } from '@app/i18n';
import { useForm } from 'react-hook-form';
import { Drawer, ErrorBanner, LoadingButton } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { userDefaults, type User, type UserFormValues } from './UserModel';
import { useUserValidation } from './useFormValidation';
import { UserForm } from './UserForm';

export interface UserDrawerProps {
  open: boolean;
  /** Ausente = **Novo usuário**. */
  usuario?: User;
  /** Erro da última tentativa de gravar (`email_duplicado`, `ultimo_admin`, …). */
  erro?: ApiError | null;
  pending?: boolean;
  onSubmit: (valores: UserFormValues) => void;
  onClose: () => void;
}

/**
 * Drawer de criar/editar usuário (T22, FR-131).
 *
 * O estado do formulário mora aqui porque o rodapé do drawer é quem submete: o
 * botão vive fora do `<form>` e o alcança por `form=`. Fechar com alteração
 * pendente pede confirmação — quem cuida disso é o próprio `Drawer`.
 */
export function UserDrawer({
  open,
  usuario,
  erro,
  pending = false,
  onSubmit,
  onClose,
}: UserDrawerProps) {
  const formId = useId();
  const editando = Boolean(usuario);

  const { control, handleSubmit, watch, formState, reset } = useForm<UserFormValues>({
    defaultValues: userDefaults(usuario),
    ...useUserValidation(),
  });

  function close() {
    reset(userDefaults(usuario));
    onClose();
  }

  return (
    <Drawer
      open={open}
      title={t(editando ? 'users.drawer.title.edit' : 'users.drawer.title.new')}
      subtitle={t(editando ? 'users.drawer.subtitle.edit' : 'users.drawer.subtitle.new')}
      dirty={formState.isDirty && !pending}
      onClose={close}
      discardMessage={t('users.drawer.discard')}
      footer={
        <LoadingButton
          type="submit"
          form={formId}
          loading={pending}
          disabled={!formState.isValid}
          loadingLabel={t(
            editando ? 'users.drawer.submitting.edit' : 'users.drawer.submitting.new',
          )}
        >
          {t(editando ? 'users.drawer.submit.edit' : 'users.drawer.submit.new')}
        </LoadingButton>
      }
    >
      {erro ? (
        // `email_duplicado` e `ultimo_admin` mantêm o formulário como está (FR-123).
        <ErrorBanner
          error={erro}
          escrita
          title={t(editando ? 'users.drawer.error.edit' : 'users.drawer.error.new')}
          hideLink
        />
      ) : null}

      <UserForm
        control={control}
        editando={editando}
        modoSenha={watch('modoSenha')}
        formId={formId}
        onSubmit={handleSubmit(onSubmit)}
      />
    </Drawer>
  );
}
